import { useEffect } from "react";

/**
 * Sincronizzazione in tempo reale via WebSocket (/api/ws).
 *
 * Una sola connessione per l'intera app (singleton di modulo, con conteggio
 * dei mount per reggere StrictMode). Quando arriva un segnale dal server:
 *  - viene emesso "realtime:evento" (per chi vuole i dettagli),
 *  - e, con un debounce che raggruppa le raffiche, l'evento globale
 *    "app:refresh" a cui TUTTE le pagine sono già iscritte tramite
 *    useRefreshOnPull: ogni schermata ricarica i propri dati con le sue
 *    fetch REST (che applicano i permessi), senza ricaricare la pagina.
 *
 * Robustezza: riconnessione con backoff esponenziale + jitter (1s → 30s),
 * ping applicativo ogni 25s (timeout di inattività dei proxy), riaggancio
 * immediato quando si torna online o l'app torna in foreground.
 */

function wsUrl() {
  // URL relativo al dominio corrente. REACT_APP_BACKEND_URL è valorizzato
  // solo in sviluppo; in produzione è "" (same-origin dietro Traefik).
  const base = process.env.REACT_APP_BACKEND_URL;
  if (base) {
    const u = new URL(base);
    return `${u.protocol === "https:" ? "wss" : "ws"}://${u.host}/api/ws`;
  }
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/api/ws`;
}

let socket = null;
let montati = 0;            // quanti componenti usano l'hook (per aprire/chiudere)
let tentativi = 0;          // fallimenti consecutivi (per il backoff)
let timerRiconnessione = null;
let timerPing = null;
let timerDebounce = null;
let chiusuraVoluta = false;

function emetti(evento) {
  window.dispatchEvent(new CustomEvent("realtime:evento", { detail: evento }));
  // Debounce: una raffica di scritture ravvicinate produce UN solo refresh
  clearTimeout(timerDebounce);
  timerDebounce = setTimeout(() => {
    window.dispatchEvent(new CustomEvent("app:refresh"));
  }, 600);
}

function pianificaRiconnessione() {
  if (montati <= 0) return;
  clearTimeout(timerRiconnessione);
  const attesa = Math.min(30000, 1000 * 2 ** tentativi) + Math.random() * 500;
  tentativi += 1;
  timerRiconnessione = setTimeout(connetti, attesa);
}

function connetti() {
  if (montati <= 0) return;
  if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) return;
  chiusuraVoluta = false;
  try {
    socket = new WebSocket(wsUrl());
  } catch {
    pianificaRiconnessione();
    return;
  }

  socket.onopen = () => {
    tentativi = 0;
    clearInterval(timerPing);
    timerPing = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) socket.send("ping");
    }, 25000);
  };

  socket.onmessage = (e) => {
    if (e.data === "pong") return;
    try { emetti(JSON.parse(e.data)); } catch { /* messaggio non JSON: ignora */ }
  };

  socket.onclose = (e) => {
    clearInterval(timerPing);
    socket = null;
    if (chiusuraVoluta) return;
    // 1008 = sessione rifiutata dal server: inutile insistere, al logout/login
    // ci pensa l'interceptor HTTP (la prossima fetch riceverà 401/403).
    if (e.code === 1008) return;
    pianificaRiconnessione();
  };

  socket.onerror = () => {
    try { socket && socket.close(); } catch { /* noop */ }
  };
}

function disconnetti() {
  chiusuraVoluta = true;
  clearTimeout(timerRiconnessione);
  clearInterval(timerPing);
  clearTimeout(timerDebounce);
  try { socket && socket.close(1000); } catch { /* noop */ }
  socket = null;
  tentativi = 0;
}

/**
 * Hook di sincronizzazione realtime.
 * Montato una volta (nel Layout) tiene viva la connessione per tutta l'app.
 * `onEvento(dettaglio)` è facoltativo: per i componenti che vogliono reagire
 * ai singoli eventi invece del solo refresh globale.
 */
export function useRealtimeSync(onEvento) {
  useEffect(() => {
    montati += 1;
    if (montati === 1) connetti();

    const riaggancia = () => { tentativi = 0; if (!socket) connetti(); };
    const suVisibile = () => { if (document.visibilityState === "visible") riaggancia(); };
    window.addEventListener("online", riaggancia);
    document.addEventListener("visibilitychange", suVisibile);

    const handler = onEvento ? (e) => onEvento(e.detail) : null;
    if (handler) window.addEventListener("realtime:evento", handler);

    return () => {
      montati -= 1;
      window.removeEventListener("online", riaggancia);
      document.removeEventListener("visibilitychange", suVisibile);
      if (handler) window.removeEventListener("realtime:evento", handler);
      if (montati <= 0) disconnetti();
    };
  }, [onEvento]);
}
